/**
 * Shared UI primitives – reusable across all components.
 * 
 * DRY: The Slide-up Transition and the SlideUpModal are defined once here 
 * instead of being duplicated in FAQ.tsx, Footer.tsx, etc.
 */
import React, { useState } from 'react';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Slide,
  Typography,
} from '@mui/material';
import { TransitionProps } from '@mui/material/transitions';
import CloseIcon from '@mui/icons-material/Close';
import { labelTagSx, fontFamily, colors } from './tokens';

// ─── Slide-up Transition (shared) ──────────────────────────────────────────

export const SlideUpTransition = React.forwardRef(function SlideUpTransition(
  props: TransitionProps & { children: React.ReactElement<any, any> },
  ref: React.Ref<unknown>,
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

// ─── Generic Slide-Up Modal ────────────────────────────────────────────────

interface SlideUpModalProps {
  open: boolean;
  onClose: () => void;
  tag: string;
  title: string;
  children: React.ReactNode;
}

export function SlideUpModal({ open, onClose, tag, title, children }: SlideUpModalProps) {
  return (
    <Dialog
      open={open}
      onClose={onClose}
      TransitionComponent={SlideUpTransition}
      keepMounted
      PaperProps={{
        sx: {
          borderRadius: '24px',
          p: 2,
          maxWidth: '500px',
          width: '100%',
        },
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box sx={labelTagSx}>{tag}</Box>
        <IconButton onClick={onClose} size="small" sx={{ color: colors.neutral400 }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography
          sx={{
            color: colors.neutral700,
            fontFamily,
            fontSize: '1.5rem',
            fontWeight: 700,
            lineHeight: 1.2,
            mb: 3,
            mt: 1,
          }}
        >
          {title}
        </Typography>
        {children}
      </DialogContent>
    </Dialog>
  );
}

// ─── useModal hook (DRY state management for modals) ───────────────────────

export function useModal<T = null>() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<T | null>(null);

  const openModal = (item?: T) => {
    if (item !== undefined) setData(item);
    setOpen(true);
  };

  const closeModal = () => {
    setOpen(false);
  };

  return { open, data, openModal, closeModal };
}
